import { useState, useEffect } from 'react'
import { createPortalSession } from '@healthshare/shared/commands/billing'
import { requireAuth } from '../wrappers/requireAuth'
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess'
import { useBackendApi } from '../hooks/useBackendApi'
import { formatDateInTimeZone } from '../../lib/formatDateInTimeZone'
import { useAppSelector, useAppDispatch } from '../hooks/reduxToolkit'
import { SubscriptionPlanSelector } from '../components/subscriptionPlanSelector'
import { UpgradePlanModal } from '../components/upgradePlanModal'
import { useClient } from '../providers/supabase'
import { setSubscription } from '../reduxToolkit/membershipSlice'
import { isFamilyLimitedTier } from '../../lib/rules/requiredPlan'

function Subscription() {
  const { hasSubscription, subscription } = useSubscriptionAccess()
  const api = useBackendApi()
  const client = useClient()
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(true)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  const organization = useAppSelector(state => state.membership.organization)
  const membershipId = useAppSelector(state => state.membership.membershipId)

  // Refresh subscription from database when page loads
  useEffect(() => {
    async function refreshSubscription() {
      if (!membershipId) {
        setRefreshing(false)
        return
      }

      try {
        const { data, error } = await client
          .from('subscriptions')
          .select('*')
          .eq('membership_id', membershipId)
          .maybeSingle()

        if (!error) {
          // Update Redux store with latest subscription data
          dispatch(setSubscription({ subscription: data }))
        }
      } catch (error) {
        console.error('Error refreshing subscription:', error)
      } finally {
        setRefreshing(false)
      }
    }

    refreshSubscription()
  }, [membershipId, client, dispatch])

  // Check if organization has an active subscription
  const hasActiveOrgSubscription = organization && (
    !organization.subscription_end_date ||
    new Date(organization.subscription_end_date) > new Date()
  )

  // Show loading state while refreshing
  if (refreshing) {
    return (
      <div className='row'>
        <div className='col-12'>
          <h1>Subscription Management</h1>
          <div className="loading-bar"></div>
        </div>
      </div>
    )
  }

  // Users with an active organization subscription don't need personal subscriptions
  if (hasActiveOrgSubscription) {
    return (
      <div className='row'>
        <div className='col-12'>
          <h1>Subscription Management</h1>
          <div className="alert alert-info">
            Your account is managed by your organization. You don't need a personal subscription.
          </div>
        </div>
      </div>
    )
  }

  // If organization subscription has lapsed, show a warning
  const hasLapsedOrgSubscription = organization && organization.subscription_end_date &&
    new Date(organization.subscription_end_date) <= new Date()

  const handleManageSubscription = async () => {
    setLoading(true)
    setError(null)
    try {
      const { url } = await createPortalSession(api, window.location.href)
      window.location.href = url
    } catch (err) {
      setLoading(false)
      setError('Failed to open subscription management. Please try again.')
    }
  }

  return (
    <div className='row mb-5'>
      <div className='col-12'>
        <h1>Subscription Management</h1>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {hasLapsedOrgSubscription && (
          <div className="alert alert-warning mt-3">
            <strong>Your organization's subscription has expired.</strong>{' '}
            {organization?.subscription_end_date && `It ended on ${formatDateInTimeZone(organization.subscription_end_date, { format: 'MMMM d, yyyy' })}.`}{' '}
            You can subscribe to a personal subscription to continue using the app.
          </div>
        )}

        {hasSubscription && subscription ? (
          <div className="mt-4">
            <h4>Personal Subscription</h4>
            <div className="card">
              <div className="card-body">
                {subscription.tier && (
                  <p>
                    <strong>Plan:</strong>{' '}
                    {subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1)}
                  </p>
                )}
                <p>
                  <strong>Status:</strong>{' '}
                  <span className={`badge ${subscription.status === 'active' || subscription.status === 'trialing' ? 'bg-success' : 'bg-warning'}`}>
                    {subscription.status}
                  </span>
                  {subscription.cancel_at_period_end && (
                    <span className="badge bg-warning ms-2">Cancels at period end</span>
                  )}
                </p>
                <p>
                  <strong>Current period ends:</strong>{' '}
                  {formatDateInTimeZone(subscription.current_period_end, { format: 'MMMM d, yyyy' })}
                </p>
                <button
                  className={`btn btn-primary ${loading ? 'disabled' : ''}`}
                  onClick={handleManageSubscription}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Manage Subscription in Stripe'}
                </button>
                {(subscription.status === 'active' || subscription.status === 'trialing') && isFamilyLimitedTier(subscription.tier) && (
                  <button
                    className="btn btn-outline-primary ms-2"
                    onClick={() => setShowUpgradeModal(true)}
                  >
                    Upgrade to Family Plan
                  </button>
                )}
                {(subscription.status === 'active' || subscription.status === 'trialing') && (
                  <a
                    href="/team-signup?upgrade=1"
                    className="btn btn-outline-primary ms-2"
                  >
                    Upgrade to Team Plan
                  </a>
                )}
                <p className="text-muted mt-3">
                  <small>
                    Click "Manage Subscription" to update your payment method, view invoices, or cancel your subscription.
                  </small>
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <h4>Subscribe to Continue</h4>
            <p className="text-muted mb-3">
              Get full access to the HealthShare Reimbursement Tracker with a monthly subscription.
            </p>
            <SubscriptionPlanSelector
              successUrl={window.location.origin}
              cancelUrl={window.location.href}
            />
          </div>
        )}

        <UpgradePlanModal
          show={showUpgradeModal}
          onHide={() => setShowUpgradeModal(false)}
          title="Upgrade to the Family Plan"
          message="The Family plan covers submissions for everyone in your household and lets you invite your spouse as a co-administrator."
        />
      </div>
    </div>
  )
}

export default requireAuth(Subscription)
